import Decimal from "decimal.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom-v5-compat";

import { Order, Package, PackageItem } from "@megaron/fulfillment-contracts";
import { useClientManager } from "@megaron/react-clients";
import { newUuid, Uuid } from "@megaron/uuid";

import { packageWeightLimitKg } from "./constants";
import { computeToPackItems } from "./computeToPackItems";
import { getDocsList } from "./docsList";
import {
  addItemToPackage,
  getPackageWeight,
  hasPackageChanged,
  isAddPackageDisabled,
  normalizeGtin,
} from "./orderPackingUtils";
import { useOrderPackingDerived } from "./useOrderPackingDerived";

export function useOrderPacking() {
  const queryClient = useQueryClient();
  const { orderId } = useParams<{ orderId: string }>();
  const orderQuery = useClientManager("fulfillment")
    .getOrder()
    .useQuery({ orderId: orderId as Uuid });
  const itemsQuery = useClientManager("docs").searchItems().useQuery({});

  const savePackageMutation = useClientManager("fulfillment").saveOrderPackage().useMutation({});
  const removePackageMutation = useClientManager("fulfillment").removeOrderPackage().useMutation({});
  const addTrackingMutation = useClientManager("fulfillment").addOrderTrackingToShopify().useMutation({});
  const scheduleCourierMutation = useClientManager("fulfillment").schedulePickupForOrder().useMutation({});

  const [packages, setPackages] = useState<Package[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [scannedCode, setScannedCode] = useState<string | null>(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [showNoPackageDialog, setShowNoPackageDialog] = useState(false);
  const [showOverweightDialog, setShowOverweightDialog] = useState(false);
  const [showScheduleCourierDialog, setShowScheduleCourierDialog] = useState(false);
  const [showCancelShopifyDialog, setShowCancelShopifyDialog] = useState(false);

  useEffect(() => {
    if (orderQuery.data?.packages) {
      setPackages(orderQuery.data.packages);
    }
  }, [orderQuery.data?.packages]);

  const order = orderQuery.data;
  const toPackItems = useMemo(
    () => computeToPackItems(orderQuery.data, packages),
    [orderQuery.data, packages],
  );
  const docsList = useMemo(() => getDocsList(itemsQuery.data), [itemsQuery.data]);
  const getWeight = useCallback((pkg: Package) => getPackageWeight(order, pkg), [order]);

  const derived = useOrderPackingDerived(
    order,
    packages,
    toPackItems,
    scannedCode,
    activeId,
    getWeight,
  );

  const getTotalOrderQuantity = useMemo(() => {
    const data = orderQuery.data;
    if (!data) return () => 0;
    const refunds = data.refunds ?? [];
    const refundedByVariant = new Map<string, number>();
    for (const r of refunds) {
      for (const ri of r.items ?? []) {
        refundedByVariant.set(ri.variantId, (refundedByVariant.get(ri.variantId) ?? 0) + ri.quantity);
      }
    }
    return (variantId: string) => {
      const orderTotal = (data.items ?? [])
        .filter((i) => i.variantId === variantId)
        .reduce((s, i) => s + i.quantity, 0);
      return Math.max(0, orderTotal - (refundedByVariant.get(variantId) ?? 0));
    };
  }, [orderQuery.data]);

  const getDisplayName = useCallback(
    (variantId: string, fallbackName: string) => {
      const line = orderQuery.data?.items.find((i) => i.variantId === variantId);
      const code = normalizeGtin(line?.barcode ?? null);
      if (!code) return fallbackName;
      const match = docsList.find((x) => normalizeGtin(x.gtin) === code);
      return match?.itemName ?? fallbackName;
    },
    [orderQuery.data?.items, docsList],
  );

  const persistPackage = useCallback(
    (pkg: Package) => {
      if (!orderId) return;
      const previous = orderQuery.data;
      if (previous) {
        queryClient.setQueryData(orderQuery.key, {
          ...previous,
          packages: previous.packages.map((existing) => (existing.id === pkg.id ? pkg : existing)),
        });
      }
      savePackageMutation.mutate(
        { orderId, package: pkg },
        {
          onSuccess: () => queryClient.invalidateQueries(orderQuery.key),
          onError: () => {
            if (previous) queryClient.setQueryData(orderQuery.key, previous);
          },
        },
      );
    },
    [orderId, orderQuery.data, orderQuery.key, queryClient, savePackageMutation],
  );

  const handleAddPackage = useCallback(() => {
    const nextId = packages.length + 1;
    const newPkg: Package = {
      id: newUuid(),
      orderId: orderId ?? "",
      name: `Paczka #${nextId}`,
      items: [],
      size: null,
      isLabelScanned: false,
      trackingNumber: null,
      shipmentId: null,
      trackingStatus: null,
      dispatchOrderId: null,
    };
    const updated: Package[] = [...packages, newPkg];
    setPackages(updated);
    persistPackage(newPkg);
    if (activeId === null) setActiveId(nextId);
  }, [packages, orderId, activeId, persistPackage]);

  const handleQuantityChange = useCallback(
    (updated: Package[]) => {
      const prevById = new Map(packages.map((p) => [p.id, p]));
      const removed = packages.filter((p) => !updated.some((u) => u.id === p.id));
      const changed = updated.filter((p) => {
        const prev = prevById.get(p.id);
        return !prev || hasPackageChanged(prev, p);
      });
      setPackages(updated);
      if (!orderId) return;
      changed.forEach((pkg) => persistPackage(pkg));
      removed.forEach((pkg) =>
        removePackageMutation.mutate(
          { orderId, packageId: pkg.id },
          { onSuccess: () => queryClient.invalidateQueries(orderQuery.key) },
        ),
      );
    },
    [packages, orderId, persistPackage, removePackageMutation, queryClient, orderQuery.key],
  );

  const closeScanDialog = useCallback(() => {
    setDialogOpen(false);
    setScannedCode(null);
  }, []);

  const handleConfirmScan = useCallback(() => {
    if (!scannedCode || activeId === null) {
      if (activeId === null) {
        closeScanDialog();
        setShowNoPackageDialog(true);
      }
      return;
    }
    const itemToPack = toPackItems.find((i) => i.barcode === scannedCode);
    if (!itemToPack || itemToPack.quantity < 1) {
      closeScanDialog();
      return;
    }
    const currentPackage = packages[activeId - 1];
    const currentWeight = getWeight(currentPackage);
    const projectedWeight = new Decimal(currentWeight).plus(Number(itemToPack.weight ?? 0)).toNumber();
    if (projectedWeight > packageWeightLimitKg) {
      closeScanDialog();
      setShowOverweightDialog(true);
      return;
    }
    const updated = packages.map((pkg, i) =>
      i === activeId - 1 ? addItemToPackage(pkg, itemToPack) : pkg,
    );
    setPackages(updated);
    if (orderId) persistPackage(updated[activeId - 1]);
    closeScanDialog();
  }, [
    scannedCode,
    activeId,
    toPackItems,
    packages,
    getWeight,
    orderId,
    persistPackage,
    closeScanDialog,
  ]);

  return {
    orderQuery,
    orderId,
    order,
    packages,
    setPackages,
    activeId,
    setActiveId,
    toPackItems,
    scannedCode,
    setScannedCode,
    isDialogOpen,
    setDialogOpen,
    showNoPackageDialog,
    setShowNoPackageDialog,
    showOverweightDialog,
    setShowOverweightDialog,
    showScheduleCourierDialog,
    setShowScheduleCourierDialog,
    showCancelShopifyDialog,
    setShowCancelShopifyDialog,
    queryClient,
    getTotalOrderQuantity,
    getDisplayName,
    getWeight,
    addItemToPackage,
    persistPackage,
    handleAddPackage,
    handleQuantityChange,
    closeScanDialog,
    handleConfirmScan,
    ...derived,
    isAddPackageDisabled,
    scheduleCourierMutation,
    addTrackingMutation,
  };
}
